import { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Sparkles, Layers, Zap, FileDown, X } from "lucide-react";

const DISMISS_KEY = "welcome_hero_dismissed_v1";

/**
 * Empty-state welcome hero shown to first-time users (dashboard with 0
 * projects). Presents two clear paths to their first plan: pick a pre-filled
 * catalog template, or start from a blank canvas. Auto-vanishes as soon as
 * the user creates any project. Optionally dismissable — the choice persists
 * per-device via localStorage.
 */
export function WelcomeHero({ lang, onStartBlank, canCreate }) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const c = lang === "es" ? {
    eyebrow: "Bienvenido",
    title: "Empecemos por lo importante: tu idea.",
    subtitle: "Tu plan de negocio a 5 años, listo en una tarde. Elige por dónde arrancas:",
    catalog: {
      label: "Empieza de una plantilla",
      desc: "12 modelos pre-rellenados (café, SaaS, ONG, productora, marketplace…). Copia uno y adáptalo en minutos.",
      cta: "Ver el catálogo →",
    },
    scratch: {
      label: "Empieza en blanco",
      desc: "Lienzo vacío guiado por 11 módulos. La IA te sugiere ideas por cada sección.",
      cta: "Nuevo plan →",
      locked: "Ver planes",
    },
    features: ["11 módulos", "IA integrada", "Pitch deck PPTX/PDF", "Proyecciones 5 años"],
    close: "No mostrar",
  } : {
    eyebrow: "Welcome",
    title: "Let's start with the important part: your idea.",
    subtitle: "Your 5-year business plan, ready in one afternoon. Pick where to start:",
    catalog: {
      label: "Start from a template",
      desc: "12 pre-filled models (café, SaaS, non-profit, production house, marketplace…). Copy one and adapt it in minutes.",
      cta: "Browse the catalog →",
    },
    scratch: {
      label: "Start from scratch",
      desc: "Empty canvas guided by 11 modules. AI suggests ideas for every section.",
      cta: "New plan →",
      locked: "See plans",
    },
    features: ["11 modules", "AI assistant", "Pitch deck PPTX/PDF", "5-year projections"],
    close: "Hide",
  };

  return (
    <section
      className="relative overflow-hidden mb-8 border border-[#E8E6E1] rounded-sm bg-white"
      data-testid="welcome-hero"
    >
      {/* Decorative diagonal gradient wash — subtle so it doesn't dominate */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          background: "linear-gradient(135deg, #2563EB 0%, #7C3AED 45%, transparent 70%)",
        }}
      />
      {/* Grid of tiny dots for texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.35] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, #E8E6E1 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
      />

      <button
        onClick={dismiss}
        className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#A8A6A0] hover:text-[#5C5B5A] transition-colors"
        data-testid="welcome-hero-dismiss"
        aria-label={c.close}
      >
        <span>{c.close}</span>
        <X className="h-3 w-3" strokeWidth={1.5} />
      </button>

      <div className="relative px-6 sm:px-10 pt-10 pb-8">
        {/* Header */}
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#7C3AED] mb-4">
            <Sparkles className="h-3 w-3" strokeWidth={2} />
            <span>{c.eyebrow}</span>
          </div>
          <h2
            className="text-3xl sm:text-4xl leading-[1.05] text-[#1A1A1A]"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            {c.title}
          </h2>
          <p className="mt-3 text-sm sm:text-base text-[#5C5B5A] max-w-xl">{c.subtitle}</p>
        </div>

        {/* Two paths */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <PathCard
            testId="welcome-path-catalog"
            icon={<BookOpen className="h-5 w-5" strokeWidth={1.5} />}
            label={c.catalog.label}
            desc={c.catalog.desc}
            cta={c.catalog.cta}
            to="/catalog"
            variant="primary"
          />
          <PathCard
            testId="welcome-path-scratch"
            icon={<Layers className="h-5 w-5" strokeWidth={1.5} />}
            label={c.scratch.label}
            desc={c.scratch.desc}
            cta={canCreate ? c.scratch.cta : c.scratch.locked}
            onClick={canCreate ? onStartBlank : undefined}
            to={canCreate ? undefined : "/pricing"}
            variant="secondary"
          />
        </div>

        {/* Feature strip */}
        <div className="mt-8 pt-6 border-t border-[#F0EFE9] flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] text-[#8A8886] font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          {c.features.map((f, i) => (
            <span key={f} className="inline-flex items-center gap-1.5">
              {i === 0 ? <Layers className="h-3 w-3" strokeWidth={1.5} /> :
               i === 1 ? <Sparkles className="h-3 w-3" strokeWidth={1.5} /> :
               i === 2 ? <FileDown className="h-3 w-3" strokeWidth={1.5} /> :
                         <Zap className="h-3 w-3" strokeWidth={1.5} />}
              {f}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function PathCard({ icon, label, desc, cta, to, onClick, variant, testId }) {
  const primary = variant === "primary";
  const outerClass = primary
    ? "group relative overflow-hidden border border-[#2563EB]/30 bg-gradient-to-br from-[#2563EB]/5 via-white to-[#7C3AED]/5 rounded-sm p-6 hover:border-[#2563EB]/60 hover:shadow-sm transition-all"
    : "group relative overflow-hidden border border-[#E8E6E1] bg-white rounded-sm p-6 hover:border-[#1A1A1A]/60 hover:shadow-sm transition-all";
  const iconClass = primary
    ? "inline-flex items-center justify-center h-9 w-9 rounded-sm bg-gradient-to-br from-[#2563EB] to-[#7C3AED] text-white shadow-sm"
    : "inline-flex items-center justify-center h-9 w-9 rounded-sm bg-[#1A1A1A] text-white";
  const ctaClass = primary
    ? "mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#2563EB] group-hover:text-[#7C3AED] transition-colors"
    : "mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#1A1A1A] group-hover:text-[#2563EB] transition-colors";

  const inner = (
    <>
      <div className={iconClass}>{icon}</div>
      <h3 className="mt-4 text-lg text-[#1A1A1A]" style={{ fontFamily: '"Playfair Display", serif' }}>
        {label}
      </h3>
      <p className="mt-2 text-sm text-[#5C5B5A] leading-relaxed">{desc}</p>
      <div className={ctaClass}>{cta}</div>
    </>
  );

  if (to) {
    return (
      <Link to={to} className={outerClass} data-testid={testId}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={`${outerClass} text-left w-full`} data-testid={testId}>
      {inner}
    </button>
  );
}
